/**
 * Promo Code Handler
 * Automatically adds products to cart based on URL query parameters
 * 
 * Usage: Add ?pr=PROMO_CODE to URL
 * Example: ?pr=DEEPTISSUE
 */

class PromoCodeHandler {
  constructor() {
    // Configuration: Map promo codes to product/variant IDs
    // Add new promo codes here as needed
    this.promoConfig = {
      'DEEPTISSUE': {
        variantId: 40320212926487,
        quantity: 1,
        properties: {
          _PromoCode: 'DEEPTISSUE'
        }
      },
      // Add more promo codes here:
      'GIFT': {
        variantId: 40320212926487,
        quantity: 1,
        properties: {
          _PromoCode: 'GIFT'
        }
      },
      'STRETCH': {
        variantId: 44155652964375,
        quantity: 1,
        properties: {
          _PromoCode: 'STRETCH'
        }
      },
      'MINI': {
        variantId: 41621944533015,
        quantity: 1,
        properties: {
          _PromoCode: 'MINI'
        }
      },
      'WHEEL4FOCUS': {
        variantId: 39434982293527,
        quantity: 1,
        properties: {
          _PromoCode: 'WHEEL4FOCUS'
        }
      }
    };

    this.processedPromos = new Set(); // Track processed promos to avoid duplicates
    this.init();
  }

  /**
   * Initialize the promo code handler
   */
  init() {
    // Wait for DOM to be ready, and add a delay to allow any discount routes
    // or other cart operations to finish processing first
    const delay = 3000; // 3 second delay to ensure cart state is stable

    const initCheck = () => {
      setTimeout(() => {
        this.checkAndProcessPromo();
      }, delay);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initCheck);
    } else {
      initCheck();
    }
  }

  /**
   * Get URL query parameters
   */
  getQueryParams() {
    const params = new URLSearchParams(window.location.search);
    return params;
  }

  /**
   * Check for promo code in URL and process if found
   */
  async checkAndProcessPromo() {
    const params = this.getQueryParams();
    const promoCode = params.get('pr');

    if (!promoCode) {
      return; // No promo code in URL
    }

    // Normalize promo code to uppercase for case-insensitive matching
    const normalizedCode = promoCode.toUpperCase();

    // Check if this promo has already been processed (prevent duplicates)
    if (this.processedPromos.has(normalizedCode)) {
      return;
    }

    // Check if promo code exists in configuration
    const promoConfig = this.promoConfig[normalizedCode];
    if (!promoConfig) {
      console.warn(`PromoCodeHandler: Unknown promo code "${promoCode}"`);
      return;
    }

    // Check if variant ID is configured
    if (!promoConfig.variantId) {
      console.warn(`PromoCodeHandler: Variant ID not configured for promo code "${normalizedCode}"`);
      return;
    }

    // Check if variant is already in cart before processing
    const isInCart = await this.isVariantInCart(promoConfig.variantId);
    if (isInCart) {
      console.log(`PromoCodeHandler: Variant ${promoConfig.variantId} already in cart, skipping addition for promo code "${normalizedCode}"`);
      // Mark as processed to prevent future attempts
      this.processedPromos.add(normalizedCode);
      return;
    }

    // Process the promo code
    this.processPromo(normalizedCode, promoConfig);
  }

  /**
   * Check if a variant is already in the cart
   */
  async isVariantInCart(variantId) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!response.ok) {
        console.warn('PromoCodeHandler: Failed to fetch cart, assuming variant not in cart');
        return false;
      }

      const cart = await response.json();
      
      // Check if variant exists in cart items
      // Convert variantId to string for comparison since Shopify returns strings
      const variantIdStr = String(variantId);
      const isInCart = cart.items.some(item => String(item.variant_id) === variantIdStr);
      
      return isInCart;
    } catch (error) {
      console.error('PromoCodeHandler: Error checking cart:', error);
      // On error, assume not in cart to allow the add to proceed
      return false;
    }
  }

  /**
   * Process a promo code by adding the product to cart
   */
  async processPromo(promoCode, config) {
    // Mark as processed to prevent duplicate additions
    this.processedPromos.add(promoCode);

    try {
      // Double-check cart one more time right before adding (in case discount route added it)
      const isInCart = await this.isVariantInCart(config.variantId);
      if (isInCart) {
        console.log(`PromoCodeHandler: Variant ${config.variantId} already in cart, skipping addition for promo code "${promoCode}"`);
        // Update cart UI anyway to ensure it's refreshed
        if (typeof CartManager !== 'undefined') {
          CartManager.reloadCartCount();
          CartManager.reloadMainCart();
        }
        return;
      }

      // Prepare cart data
      const cartData = {
        items: [
          {
            id: config.variantId,
            quantity: config.quantity || 1,
            properties: config.properties || {}
          }
        ]
      };

      // Add to cart
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartData)
      });

      // Handle 409 Conflict - check if item was actually added
      if (response.status === 409) {
        // Wait a moment for cart to settle, then check if item is in cart
        await new Promise(resolve => setTimeout(resolve, 500));
        const isNowInCart = await this.isVariantInCart(config.variantId);
        
        if (isNowInCart) {
          // Item is in cart, treat as success
          console.log(`PromoCodeHandler: Variant ${config.variantId} is in cart (409 conflict resolved), treating as success for promo code "${promoCode}"`);
          
          // Update cart UI
          if (typeof CartManager !== 'undefined') {
            CartManager.reloadCartCount();
            CartManager.reloadMainCart();
          }

          // Dispatch success event
          document.dispatchEvent(new CustomEvent('promo:processed', {
            detail: { promoCode, conflictResolved: true }
          }));
          return;
        } else {
          // 409 but item not in cart - actual error
          const errorData = await response.json().catch(() => ({ message: 'Conflict: Item may be unavailable or cart state invalid' }));
          throw new Error(errorData.message || 'HTTP 409 Conflict');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // MAI Add to Cart Tracking (if available)
      if (typeof MAI !== 'undefined' && result.items && result.items[0]) {
        const item = result.items[0];
        MAI.emit('addToCart', item.price / 100, item.price_currency || 'USD', item.quantity, {
          productId: String(item.product_id),
          productName: item.product_title,
          productType: item.product_type,
          productVendor: item.vendor,
          variantId: String(item.variant_id),
          variantName: item.variant_title,
          promoCode: promoCode
        });
      }

      // Update cart UI
      if (typeof CartManager !== 'undefined') {
        CartManager.reloadCartCount();
        CartManager.reloadMainCart();
        // Optionally open cart drawer
        // CartManager.openCart();
      }

      console.log(`PromoCodeHandler: Successfully added product for promo code "${promoCode}"`);

      // Dispatch custom event for other scripts to listen to
      document.dispatchEvent(new CustomEvent('promo:processed', {
        detail: { promoCode, result }
      }));

    } catch (error) {
      console.error(`PromoCodeHandler: Error processing promo code "${promoCode}":`, error);
      
      // Remove from processed set so it can be retried
      this.processedPromos.delete(promoCode);

      // Dispatch error event
      document.dispatchEvent(new CustomEvent('promo:error', {
        detail: { promoCode, error: error.message }
      }));
    }
  }

  /**
   * Manually add a promo code (for programmatic use)
   */
  async addPromo(promoCode) {
    const normalizedCode = promoCode.toUpperCase();
    const promoConfig = this.promoConfig[normalizedCode];
    
    if (!promoConfig) {
      throw new Error(`Promo code "${promoCode}" not found in configuration`);
    }

    return this.processPromo(normalizedCode, promoConfig);
  }
}

// Initialize the promo code handler
const promoCodeHandler = new PromoCodeHandler();

// Make it globally available for programmatic access
window.PromoCodeHandler = promoCodeHandler;

